import { useState, useEffect, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { FaArrowLeft } from 'react-icons/fa'
import { authService, sendCompleteRegistrationEvent, analyticsService } from '../services'
import { useTranslation } from 'react-i18next'
import { usePageTitle, setDocumentTitle } from '../utils/translation'

export default function PhoneVerify() {
  const { t } = useTranslation()
  const [code, setCode] = useState(['', '', '', '', '', ''])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [countdown, setCountdown] = useState(60)
  const [canResend, setCanResend] = useState(false)
  const [resending, setResending] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()
  const inputRefs = useRef<(HTMLInputElement | null)[]>([null, null, null, null, null, null])
  const hiddenInputRef = useRef<HTMLInputElement>(null)
  const submitTimeoutRef = useRef<number | null>(null)
  
  // Get translated page title
  const pageTitle = usePageTitle('phoneVerify.pageTitle', 'Verify Phone')
  
  // Get verification information from location state
  const verificationType = location.state?.registrationType || 'phone'
  const contactInfo = location.state?.contactInfo || ''
  const returnTo = location.state?.returnTo || '/flowers'

  useEffect(() => {
    // Set document title
    setDocumentTitle(pageTitle)
    
    // Start countdown for resend code
    if (countdown > 0 && !canResend) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000)
      return () => clearTimeout(timer)
    } else if (countdown === 0 && !canResend) {
      setCanResend(true)
    }
  }, [countdown, canResend, pageTitle])

  useEffect(() => {
    // Cleanup timeout on unmount
    return () => {
      if (submitTimeoutRef.current !== null) {
        clearTimeout(submitTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    // Only proceed if not already submitting
    if (isSubmitting) return;
    
    // Check if all code digits are filled
    if (code.every(digit => digit !== '')) {
      // Clear any existing timeout
      if (submitTimeoutRef.current !== null) {
        clearTimeout(submitTimeoutRef.current);
      }
      
      // Auto-submit after 0.5 seconds when all digits are filled
      submitTimeoutRef.current = window.setTimeout(() => {
        submitTimeoutRef.current = null;
        if (!loading && !isSubmitting) {
          handleSubmit(new Event('submit') as unknown as React.FormEvent);
        }
      }, 500) as unknown as number;
      
      return () => {
        if (submitTimeoutRef.current !== null) {
          clearTimeout(submitTimeoutRef.current);
        }
      };
    }
  }, [code, loading, isSubmitting]);

  const handleBack = () => {
    navigate(-1)
  }

  const handleResendCode = async () => {
    if (!canResend || !contactInfo) return
    
    // Reset countdown and disable resend button
    setResending(true)
    setError('')
    
    try {
      // Send appropriate request based on verification type
      if (verificationType === 'login') {
        await authService.requestLoginCode(contactInfo)
      } else {
        // For registration
        await authService.registerWithPhone(contactInfo)
      }
      
      // Reset the countdown
      setCountdown(60)
      setCanResend(false)
      setResending(false)
    } catch (err: any) {
      console.error('Resend code error:', err)
      let errorMessage = t('phoneVerify.errors.resendFailed')
      
      if (err.message) {
        errorMessage = err.message;
      } else if (err.response && err.response.data) {
        // Handle API error responses
        const responseError = err.response.data.error || err.response.data.message;
        if (responseError) {
          errorMessage = responseError;
        }
      }
      
      setError(errorMessage)
      setResending(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    // Prevent duplicate submissions
    if (loading || isSubmitting) return;
    
    const verificationCode = code.join('')
    if (verificationCode.length !== 6) {
      setError(t('phoneVerify.errors.incompleteCode'))
      return
    }
    
    setLoading(true)
    setIsSubmitting(true)
    setError('')
    
    try {
        // Verify login code
        await authService.verifyLoginCode(contactInfo, verificationCode)
        
        // Track phone verification completion for registration
        if (verificationType === 'phone') {
          try {
            analyticsService.trackAccountCreation('phone');
          } catch (analyticsError) {
            console.error('Error tracking phone verification completion:', analyticsError);
            // Don't block the registration flow if analytics tracking fails
          }
        }
        
        // Send Facebook Conversions API event for phone registration
        try {
          await sendCompleteRegistrationEvent({
            phone: contactInfo,
            clientUserAgent: navigator.userAgent
          }, window.location.href);
        } catch (eventError) {
          console.error('Error sending Facebook event:', eventError);
          // Don't block the registration flow if event tracking fails
        }
      
      // Navigate to return path or default to flowers page
      navigate(returnTo)
    } catch (err: any) {
      console.error('Verification error:', err)
      let errorMessage = t('phoneVerify.errors.invalidCode')
      
      // Extract error message from various possible error structures
      if (err.message) {
        errorMessage = err.message;
      } 
      
      if (err.response) {
        if (err.response.data) {
          if (typeof err.response.data === 'string') {
            errorMessage = err.response.data;
          } else if (err.response.data.error) {
            errorMessage = err.response.data.error;
          } else if (err.response.data.message) {
            errorMessage = err.response.data.message;
          }
        } else if (err.response.status === 400 || err.response.status === 401) {
          errorMessage = t('phoneVerify.errors.checkAndTryAgain');
        }
      }
      
      setError(errorMessage)
      
      // Clear the code input fields to allow the user to try again
      setCode(['', '', '', '', '', ''])
      // Focus on the first input field
      inputRefs.current[0]?.focus()
    } finally {
      setLoading(false)
      setTimeout(() => {
        setIsSubmitting(false)
      }, 1000) // Prevent rapid re-submissions
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    // Handle backspace for continuous deletion
    if (e.key === 'Backspace' && index > 0 && !code[index]) {
      // If current field is empty and backspace is pressed, move to previous field
      const newCode = [...code];
      newCode[index - 1] = '';
      setCode(newCode);
      inputRefs.current[index - 1]?.focus();
    }
  };

  // Handle input from the hidden field (iOS autocomplete)
  const handleHiddenInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Check if the input is a 6-digit number
    if (value.length <= 6 && /^\d+$/.test(value)) {
      // Cancel any existing submit timeout
      if (submitTimeoutRef.current !== null) {
        clearTimeout(submitTimeoutRef.current);
        submitTimeoutRef.current = null;
      }
      
      // Fill the visible input fields with the digits
      const digits = value.split('');
      const newCode = [...code];
      
      // Fill as many digits as we have
      for (let i = 0; i < digits.length && i < 6; i++) {
        newCode[i] = digits[i];
      }
      
      setCode(newCode);
      
      // Only auto-focus if not all fields are filled to prevent keyboard pop-up
      if (digits.length < 6) {
        // Focus on the next empty field
        const nextEmptyIndex = Math.min(digits.length, 5);
        inputRefs.current[nextEmptyIndex]?.focus();
      }
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <div className="p-6 flex-1">
        <div className="flex items-center mb-8">
          <button onClick={handleBack} className="mr-4 p-2 bg-gray-200 rounded-full">
            <FaArrowLeft className="text-gray-700 text-xl font-bold" />
          </button>
        </div>

        <h2 className="text-2xl font-semibold text-center mb-2">
          {verificationType === 'email' 
            ? t('phoneVerify.title.email') 
            : t('phoneVerify.title.phone')}
        </h2>
        
        <p className="text-gray-700 text-center mb-[50px]">
          {contactInfo && (
            <span className="font-medium block mb-2">
              {contactInfo.startsWith('66') ? 
                `+${contactInfo}` : 
                contactInfo}
            </span>
          )}
          {t('phoneVerify.securityCodeSent')}
        </p>

        <form onSubmit={handleSubmit} className="flex flex-col items-center">
          {error && (
            <div className="w-full p-3 mb-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-center text-red-600 font-medium">{error}</p>
            </div>
          )}
          
          {/* Hidden input for iOS autocomplete */}
          <input
            ref={hiddenInputRef}
            type="number"
            inputMode="numeric"
            autoComplete="one-time-code"
            className="opacity-0 h-0 w-0 absolute"
            onChange={handleHiddenInputChange}
            pattern="[0-9]*"
          />
          
          <div className="grid grid-cols-6 gap-2 mb-[50px]">
            {code.map((digit, i) => (
              <input
                key={i}
                type="tel"
                maxLength={1}
                value={digit}
                ref={el => inputRefs.current[i] = el}
                className={`w-12 h-12 text-center border rounded-lg focus:outline-none focus:border-yellow-400 ${
                  error ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-yellow-100'
                }`}
                onChange={(e) => {
                  const newCode = [...code]
                  newCode[i] = e.target.value
                  setCode(newCode)
                  if (e.target.value && i < 5) {
                    inputRefs.current[i + 1]?.focus()
                  }
                }}
                onKeyDown={(e) => handleKeyDown(e, i)}
                onPaste={(e) => {
                  e.preventDefault()
                  const pastedData = e.clipboardData.getData('text')
                  if (pastedData.length === 6 && /^\d+$/.test(pastedData)) {
                    const digits = pastedData.split('')
                    setCode(digits)
                  }
                }}
              />
            ))}
          </div>

          <p className="text-center text-gray-700 mb-6">
            {t('phoneVerify.didntReceive')}{' '}
            <button 
              type="button"
              onClick={handleResendCode}
              disabled={!canResend || resending}
              className={`${canResend && !resending ? 'text-blue-500' : 'text-gray-400'} cursor-pointer`}
            >
              {resending 
                ? t('phoneVerify.sending')
                : canResend 
                  ? t('phoneVerify.resendCode')
                  : t('phoneVerify.resendCountdown', { countdown })}
            </button>
          </p>

          <button 
            type="submit"
            disabled={loading || isSubmitting}
            className={`w-full h-[60px] rounded-[16px] font-medium text-black ${
              loading || isSubmitting ? 'bg-[#FDEDB6]' : 'bg-[#F9D348]'
            }`}
          >
            {loading ? t('phoneVerify.verifying') : t('phoneVerify.continue')}
          </button>
        </form>
      </div>
    </div>
  )
} 